import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Button, Box, Typography, Tooltip } from '@mui/material'
import useModals from 'src/Hooks/useModals'
import { useDecommissionInstallation } from '../hooks/use-decommission-installation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useNavigate } from 'react-router-dom'
import { Installation } from 'src/Libs/installations/models'
import { makeStyles } from 'tss-react/mui'
import { useUpdateInstallation } from '../hooks'
import { ChangeReason } from 'src/Libs/form'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { useInstallationDeletionDialog } from 'src/Libs/installations/hooks/installation-configuration/use-installation-deletion-dialog'
import { isFlowTheme } from 'src/Libs/utils'
import { tableCellButtonSx } from './table-styles'

const useStyles = makeStyles()(theme => ({
  actionButton: {
    '.MuiTypography-root': {
      fontSize: theme.typography.overline.fontSize,
    },
  },
}))

// #region DeleteInstallationButton
interface DeleteInstallationButtonProps {
  installation: Installation
}

/**
 * Button to delete (decommission) an installation. Disabled with a tooltip for the Flow theme as the action is not available yet.
 */
export const DeleteInstallationButton: React.FC<DeleteInstallationButtonProps> = ({ installation }) => {
  const { t } = useCustomTranslation()
  const { openReasonDialog } = useModals()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const navigate = useNavigate()
  const { classes } = useStyles()
  const { openDeleteInstallationConfirmationDialog } = useInstallationDeletionDialog(installation.id)

  const { mutateAsync: decommissionInstallation } = useDecommissionInstallation({
    onSuccess: () => {
      enqueueSuccessStack(t('system-details.components.delete-installation.delete-success'))
      navigate(`/overview/installations`, { replace: true })
    },
    onError: () => enqueueErrorStack(t('system-details.components.delete-installation.delete-failed')),
  })

  const onConfirmInstallationDeleting = async (payload: ChangeReason): Promise<void> => {
    await decommissionInstallation({
      installationId: installation.id,
      payload,
    })
  }

  const openDeleteOrRecommissionInstallationConfirmationDialog = (): void => {
    if (installation.installationStatus === 'planned') {
      openDeleteInstallationConfirmationDialog()
    } else {
      openReasonDialog({
        onSubmit: onConfirmInstallationDeleting,
        title: t('system-details.components.delete-installation.delete'),
        description: t('system-details.components.delete-installation.text'),
      })
    }
  }

  return (
    <Tooltip title={isFlowTheme ? t('installation-extensions.coming-soon') : undefined} placement="top-start">
      <Box component="span" sx={tableCellButtonSx}>
        <Button
          key={'delete-installation'}
          onClick={openDeleteOrRecommissionInstallationConfirmationDialog}
          variant="text"
          className={classes.actionButton}
          disabled={isFlowTheme}
        >
          <Typography variant="overline">{t('system-details.components.delete-installation.delete')}</Typography>
        </Button>
      </Box>
    </Tooltip>
  )
}
// #endregion

// #region ChangeLocationButton
interface ChangeLocationButtonProps {
  installation: Installation
  refetchInstallation: () => void
}

/**
 * Button to change the location of an installation
 */
export const ChangeLocationButton: React.FC<ChangeLocationButtonProps> = ({ installation, refetchInstallation }) => {
  const { t } = useCustomTranslation()
  const { openChangeInstallationLocationModal } = useModals()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { classes } = useStyles()

  const { mutate: updateInstallation } = useUpdateInstallation({
    onSuccess: () => {
      enqueueSuccessStack(t('installation-details.change-location.save.success'))
      refetchInstallation()
    },
    onError: error => {
      const err = error as AuthFetchError

      if (err.response?.data.message === FetchErrorCodes.OwnerNotSet.toString()) {
        enqueueErrorStack(t('installation-details.change-location.save.error-no-owner'))
      } else if (err.response?.data.message === FetchErrorCodes.OwnerHasNoAddress.toString()) {
        enqueueErrorStack(t('installation-details.change-location.save.error-no-owner-address'))
      } else {
        enqueueErrorStack(t('installation-details.change-location.save.error'))
      }

      console.error('error saving installation location', error)
    },
  })

  const openLocationChangeDialog = (): void => {
    openChangeInstallationLocationModal({
      onConfirm: payload => updateInstallation({ installationId: installation.id, payload }),
      currentCountryCode: installation.countryCode,
    })
  }

  return (
    <Box sx={tableCellButtonSx}>
      <Button key={'change-location'} onClick={openLocationChangeDialog} variant="text" className={classes.actionButton}>
        <Typography variant="overline">{t('installation-details.change-location.action.title')}</Typography>
      </Button>
    </Box>
  )
}
// #endregion
